import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUturnLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { relativeTime } from "../common/relativeTime";
import { FeatureRequestCommentItem } from "./api";

const initials = (name: string) =>
  name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

export const StaffBadge = () => <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 px-1 py-0.5 rounded">Police Narratives AI</span>;

interface ThreadProps {
  comments: FeatureRequestCommentItem[];
  isStaff: boolean;
  // Who the caller is about to reply to (top-level comment id), or null.
  replyTo: string | null;
  onReplyTo: (commentId: string | null) => void;
  onDelete: (comment: FeatureRequestCommentItem) => void;
  onSubmitReply: (parentId: string, body: string) => Promise<boolean>;
}

// Single-level threading: top-level comments in posting order, each with
// its replies indented beneath and an inline reply composer when open.
const CommentThread: React.FC<ThreadProps> = ({ comments, isStaff, replyTo, onReplyTo, onDelete, onSubmitReply }) => {
  const { topLevel, replies } = useMemo(() => {
    const replies = new Map<string, FeatureRequestCommentItem[]>();
    for (const c of comments) {
      if (!c.parent_id) continue;
      const list = replies.get(c.parent_id) || [];
      list.push(c);
      replies.set(c.parent_id, list);
    }
    return { topLevel: comments.filter((c) => !c.parent_id), replies };
  }, [comments]);

  if (topLevel.length === 0) return null;

  return (
    <div className="p-4 border rounded-lg bg-base-100 border-base-300">
      {topLevel.map((c, idx) => {
        const children = replies.get(c.id) || [];
        return (
          <div key={c.id} className={cn(idx > 0 && "mt-4 pt-4 border-t border-base-300")}>
            <CommentRow comment={c} isStaff={isStaff} onDelete={() => onDelete(c)} onReply={c.removed ? undefined : () => onReplyTo(replyTo === c.id ? null : c.id)} />
            {children.length > 0 && (
              <div className="mt-3 ml-11 pl-3 border-l-2 border-base-300 space-y-3">
                {children.map((r) => (
                  <CommentRow key={r.id} comment={r} isStaff={isStaff} onDelete={() => onDelete(r)} compact />
                ))}
              </div>
            )}
            {replyTo === c.id && (
              <div className="mt-3 ml-11">
                <ReplyComposer replyingTo={c.author.name} onCancel={() => onReplyTo(null)} onSubmit={(body) => onSubmitReply(c.id, body)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CommentRow: React.FC<{
  comment: FeatureRequestCommentItem;
  isStaff: boolean;
  onDelete: () => void;
  onReply?: () => void;
  compact?: boolean;
}> = ({ comment: c, isStaff, onDelete, onReply, compact }) => {
  if (c.removed) {
    return <p className="text-sm italic text-base-content/50">Comment removed</p>;
  }
  return (
    <div className={cn("flex gap-3", c.hidden_at && "opacity-50")}>
      <div className={cn("shrink-0 rounded-full flex items-center justify-center font-semibold", compact ? "size-6 text-[10px]" : "size-8 text-[11px]", c.is_staff ? "bg-blue-500 text-white" : "bg-base-200 text-base-content/70")}>{initials(c.author.name)}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{c.author.name}</span>
          {c.is_staff ? <StaffBadge /> : c.author.agency && <span className="text-xs text-base-content/50">{c.author.agency}</span>}
          <span className="text-xs text-base-content/40">{relativeTime(c.created_at)}</span>
          {c.hidden_at && <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-200 text-base-content/60">Hidden</span>}
          <span className="ml-auto inline-flex items-center gap-0.5">
            {onReply && !c.hidden_at && (
              <button type="button" className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-xs text-base-content/50 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40" onClick={onReply}>
                <ArrowUturnLeftIcon className="size-3.5" /> Reply
              </button>
            )}
            {(c.isOwn || isStaff) && !c.hidden_at && (
              <button type="button" className="p-1 rounded-md text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40" title="Delete comment" onClick={onDelete}>
                <TrashIcon className="size-3.5" />
              </button>
            )}
          </span>
        </div>
        <p className="mt-1 text-sm text-base-content/90 leading-6 whitespace-pre-wrap">{c.body}</p>
      </div>
    </div>
  );
};

const ReplyComposer: React.FC<{ replyingTo: string; onCancel: () => void; onSubmit: (body: string) => Promise<boolean> }> = ({ replyingTo, onCancel, onSubmit }) => {
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const submit = async () => {
    if (!body.trim() || posting) return;
    setPosting(true);
    try {
      if (await onSubmit(body.trim())) setBody("");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <textarea
        ref={ref}
        className="textarea textarea-bordered bg-base-100 w-full text-sm leading-6 focus:outline-none focus:border-blue-500"
        rows={2}
        placeholder={`Reply to ${replyingTo}…`}
        value={body}
        maxLength={4000}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="mt-1.5 flex items-center justify-end gap-2">
        <Button variant="outline" size="xs" onClick={onCancel} disabled={posting}>
          Cancel
        </Button>
        <Button variant="primary" size="xs" onClick={submit} disabled={!body.trim() || posting}>
          {posting ? "Posting…" : "Reply"}
        </Button>
      </div>
    </div>
  );
};

export default CommentThread;
